import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  GraduationCap,
  Plane,
  Home,
  Briefcase,
  FileText,
  UserCircle,
  Stamp,
  Banknote,
  Upload,
  X,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Package,
  ClipboardList,
  Send,
  UserPlus,
  XCircle,
  Play,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { agentAPI } from '../../services/api';

// =============================================================================
// 8 FIXED SERVICES — hardcoded, not fetched from database
// =============================================================================
const SERVICES = [
  {
    serviceType: 'PROFILE_ASSESSMENT',
    name: 'Profile Assessment',
    description:
      'Comprehensive student profile evaluation and career path assessment',
    icon: UserCircle,
    gradient: 'from-blue-500 to-indigo-600',
  },
  {
    serviceType: 'UNIVERSITY_SHORTLISTING',
    name: 'Preapplication Support',
    description:
      'Documentation preparation, shortlisting, and application guidance',
    icon: FileText,
    gradient: 'from-violet-500 to-purple-600',
  },
  {
    serviceType: 'APPLICATION_ASSISTANCE',
    name: 'Apply University',
    description: 'University selection and application submission assistance',
    icon: GraduationCap,
    gradient: 'from-purple-500 to-pink-600',
  },
  {
    serviceType: 'VISA_GUIDANCE',
    name: 'Visa & Interview Support',
    description: 'Visa processing, documentation, and interview preparation',
    icon: Stamp,
    gradient: 'from-pink-500 to-rose-600',
  },
  {
    serviceType: 'SCHOLARSHIP_SEARCH',
    name: 'Ticket & Travel Support',
    description: 'Flight booking, travel planning, and logistics coordination',
    icon: Plane,
    gradient: 'from-orange-500 to-amber-600',
  },
  {
    serviceType: 'ACCOMMODATION_HELP',
    name: 'Find Accommodation',
    description: 'Student housing search, comparison, and booking assistance',
    icon: Home,
    gradient: 'from-amber-500 to-yellow-600',
  },
  {
    serviceType: 'LOAN_ASSISTANCE',
    name: 'Education Loan',
    description:
      'Financial aid options, loan processing, and documentation support',
    icon: Banknote,
    gradient: 'from-emerald-500 to-green-600',
  },
  {
    serviceType: 'PRE_DEPARTURE_ORIENTATION',
    name: 'Find Jobs Abroad',
    description: 'Job search, career placement, and work permit assistance',
    icon: Briefcase,
    gradient: 'from-teal-500 to-cyan-600',
  },
];

// =============================================================================
// STATUS DISPLAY CONFIGURATIONS
// =============================================================================

// Derive button state from a ServiceRequest document
function getButtonState(request) {
  if (!request) return 'APPLY';
  if (
    request.agentApprovalStatus === 'REJECTED' ||
    request.status === 'CANCELLED'
  )
    return 'REJECTED';
  if (request.status === 'COMPLETED') return 'COMPLETED';
  if (
    request.isAgentInitiated &&
    request.agentApprovalStatus === 'PENDING_APPROVAL'
  )
    return 'PENDING_APPROVAL';
  // ASSIGNED, IN_PROGRESS, WAITING_STUDENT, ON_HOLD — agent can work (approved or admin-assigned)
  return 'ACTIVE';
}

const BUTTON_DISPLAY = {
  APPLY: { label: 'Refer Student', variant: 'primary', icon: Send },
  PENDING_APPROVAL: {
    label: 'Pending Approval',
    variant: 'warning',
    icon: Clock,
  },
  ACTIVE: { label: 'In Progress', variant: 'info', icon: Play },
  COMPLETED: { label: 'Completed', variant: 'success', icon: CheckCircle2 },
  REJECTED: { label: 'Rejected', variant: 'error', icon: XCircle },
};

const VARIANT_CLASSES = {
  primary: 'bg-emerald-500 hover:bg-emerald-600 text-white',
  warning:
    'bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200',
  info: 'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200',
  success: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  error: 'bg-red-100 text-red-700 border border-red-200 hover:bg-red-200',
};

// Table status badge config
const TABLE_STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: {
    label: 'Pending',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
  },
  ASSIGNED: {
    label: 'Assigned',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: UserCircle,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    icon: Play,
  },
  WAITING_STUDENT: {
    label: 'Awaiting Student',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: Clock,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: CheckCircle2,
  },
  ON_HOLD: {
    label: 'On Hold',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: AlertCircle,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: XCircle,
  },
};

const APPROVAL_BADGE = {
  PENDING_APPROVAL: {
    label: 'Pending Approval',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  },
  APPROVED: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700 border-green-200',
  },
  REJECTED: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700 border-red-200',
  },
};

// Service name lookup from serviceType
const SERVICE_NAME_MAP = {};
SERVICES.forEach(s => {
  SERVICE_NAME_MAP[s.serviceType] = s.name;
});

function formatDate(date) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// COMPONENT
// =============================================================================
export default function AgentExploreProducts() {
  const [activeTab, setActiveTab] = useState('marketplace');

  // Service request data for button states
  const [allServiceRequests, setAllServiceRequests] = useState([]);
  const [serviceRequestMap, setServiceRequestMap] = useState({}); // key: `${studentId}_${serviceType}`
  const [statusLoading, setStatusLoading] = useState(true);

  // Agent's students for dropdown
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);

  // Modal state
  const [referModalOpen, setReferModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [referForm, setReferForm] = useState({
    studentId: '',
    notes: '',
    documents: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Create new student (inline in modal)
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [creatingStudent, setCreatingStudent] = useState(false);

  // My Service Requests tab
  const [myRequests, setMyRequests] = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [requestSearch, setRequestSearch] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');

  // ---------------------------------------------------------------------------
  // DATA FETCHING
  // ---------------------------------------------------------------------------

  const fetchServiceStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const response = await agentAPI.getExploreServiceStatus();
      const requests = response.serviceRequests || [];
      setAllServiceRequests(requests);

      const map = {};
      requests.forEach(sr => {
        const key = `${sr.studentId}_${sr.serviceType}`;
        if (
          !map[key] ||
          new Date(sr.createdAt) > new Date(map[key].createdAt)
        ) {
          map[key] = sr;
        }
      });
      setServiceRequestMap(map);
    } catch (error) {
      console.error('Failed to fetch service status:', error);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      setStudentsLoading(true);
      const response = await agentAPI.getStudentsList();
      setStudents(response.students || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    try {
      setMyRequestsLoading(true);
      const response = await agentAPI.getServiceRequests({ limit: 100 });
      setMyRequests(response.serviceRequests || []);
    } catch (error) {
      console.error('Failed to fetch service requests:', error);
      toast.error('Failed to load service requests');
    } finally {
      setMyRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceStatus();
    fetchStudents();
  }, [fetchServiceStatus, fetchStudents]);

  useEffect(() => {
    if (activeTab === 'requests') fetchMyRequests();
  }, [activeTab, fetchMyRequests]);

  // ---------------------------------------------------------------------------
  // COMPUTED: per-service summary for card badges
  // ---------------------------------------------------------------------------

  const serviceSummary = useMemo(() => {
    const summary = {};
    SERVICES.forEach(s => {
      const matching = allServiceRequests.filter(
        sr => sr.serviceType === s.serviceType,
      );
      const pending = matching.filter(
        sr =>
          sr.isAgentInitiated && sr.agentApprovalStatus === 'PENDING_APPROVAL',
      ).length;
      const active = matching.filter(sr => {
        const state = getButtonState(sr);
        return state === 'ACTIVE';
      }).length;
      const completed = matching.filter(sr => sr.status === 'COMPLETED').length;
      summary[s.serviceType] = {
        total: matching.length,
        pending,
        active,
        completed,
      };
    });
    return summary;
  }, [allServiceRequests]);

  // ---------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------

  const getStudentServiceStatus = useCallback(
    (studentId, serviceType) => {
      return serviceRequestMap[`${studentId}_${serviceType}`] || null;
    },
    [serviceRequestMap],
  );

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  const handleReferClick = useCallback(service => {
    setSelectedService(service);
    setReferForm({ studentId: '', notes: '', documents: [] });
    setShowCreateStudent(false);
    setNewStudentForm({ firstName: '', lastName: '', email: '', phone: '' });
    setReferModalOpen(true);
  }, []);

  const handleFileSelect = useCallback(e => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 10 MB`);
        return false;
      }
      return true;
    });
    setReferForm(prev => ({
      ...prev,
      documents: [...prev.documents, ...valid],
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeDocument = useCallback(idx => {
    setReferForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== idx),
    }));
  }, []);

  // Create new student inline
  const handleCreateStudent = useCallback(async () => {
    const { firstName, lastName, email } = newStudentForm;
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error('First name, last name, and email are required');
      return;
    }
    try {
      setCreatingStudent(true);
      const response = await agentAPI.referStudent({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: newStudentForm.phone.trim(),
      });
      toast.success('Student created successfully');

      // Refresh students list and auto-select the new student
      await fetchStudents();
      const newStudent = response.student;
      if (newStudent?.studentId) {
        setReferForm(prev => ({ ...prev, studentId: newStudent.studentId }));
      }
      setShowCreateStudent(false);
      setNewStudentForm({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to create student';
      toast.error(msg);
    } finally {
      setCreatingStudent(false);
    }
  }, [newStudentForm, fetchStudents]);

  // Submit the service referral
  const handleSubmitReferral = useCallback(async () => {
    if (!referForm.studentId) {
      toast.error('Please select a student');
      return;
    }
    if (!selectedService) return;

    const { serviceType } = selectedService;
    const existing = getStudentServiceStatus(referForm.studentId, serviceType);
    if (existing && existing.status !== 'CANCELLED') {
      toast.error('This service has already been requested for this student');
      return;
    }

    try {
      setSubmitting(true);
      await agentAPI.referService(
        referForm.studentId,
        serviceType,
        referForm.notes,
        referForm.documents,
      );
      toast.success('Service request submitted! Awaiting admin approval.');
      setReferModalOpen(false);
      fetchServiceStatus();
      if (activeTab === 'requests') fetchMyRequests();
    } catch (error) {
      toast.error(
        error.response?.data?.error || 'Failed to submit service request',
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    referForm,
    selectedService,
    getStudentServiceStatus,
    fetchServiceStatus,
    fetchMyRequests,
    activeTab,
  ]);

  // Filtered requests for table
  const filteredRequests = useMemo(() => {
    return myRequests.filter(req => {
      const search = requestSearch.toLowerCase();
      const matchesSearch =
        !search ||
        (req.student?.firstName || '').toLowerCase().includes(search) ||
        (req.student?.lastName || '').toLowerCase().includes(search) ||
        (SERVICE_NAME_MAP[req.serviceType] || '')
          .toLowerCase()
          .includes(search);
      const matchesStatus =
        requestStatusFilter === 'all' || req.status === requestStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [myRequests, requestSearch, requestStatusFilter]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-explore-products">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">
            Service Marketplace
          </h1>
          <p className="text-gray-500">
            Browse services, refer students, and track your requests
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100">
            <TabsTrigger value="marketplace" className="gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Package className="w-4 h-4" />
              Services
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <ClipboardList className="w-4 h-4" />
              My Requests
              {allServiceRequests.length > 0 && (
                <Badge className="ml-1 bg-emerald-100 text-emerald-700 text-xs px-1.5">
                  {allServiceRequests.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ================================================================ */}
          {/* MARKETPLACE TAB — 8 fixed service cards                          */}
          {/* ================================================================ */}
          <TabsContent value="marketplace" className="mt-6">
            {statusLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {SERVICES.map((service, index) => {
                  const Icon = service.icon;
                  const summary = serviceSummary[service.serviceType] || {};
                  const hasActivity = summary.total > 0;

                  const gradients = [
                    'from-blue-500 to-blue-600',
                    'from-purple-500 to-violet-600',
                    'from-pink-500 to-rose-500',
                    'from-emerald-500 to-teal-600',
                    'from-orange-500 to-amber-500',
                    'from-cyan-500 to-sky-600',
                    'from-indigo-500 to-blue-700',
                    'from-fuchsia-500 to-pink-600',
                  ];

                  const gradient = gradients[index % gradients.length];

                  return (
                    <div
                      key={service.serviceType}
                      className={`
          relative overflow-hidden
          rounded-3xl
          bg-gradient-to-br ${gradient}
          p-8
          text-white
          shadow-xl
          transition-all duration-300
          hover:-translate-y-2
          hover:shadow-2xl
        `}
                    >
                      {/* Popular Badge */}
                      <div className="absolute top-6 right-6">
                        <span className="px-4 py-1 text-sm rounded-full bg-white/20 backdrop-blur-md border border-white/30">
                          Popular
                        </span>
                      </div>

                      {/* Decorative Background Icon */}
                      <div className="absolute bottom-6 right-6 opacity-10">
                        <Icon className="w-32 h-32" />
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold mb-4">
                        {service.name}
                      </h3>

                      {/* Description */}
                      <p className="text-white/90 leading-relaxed mb-6 text-sm">
                        {service.description}
                      </p>

                      {/* Features */}
                      {hasActivity && (
                        <div className="flex flex-wrap gap-2 mb-8">
                          {summary.pending > 0 && (
                            <span className="px-3 py-1 rounded-lg text-xs bg-white/20 backdrop-blur-sm border border-white/30">
                              {summary.pending} Pending
                            </span>
                          )}
                          {summary.active > 0 && (
                            <span className="px-3 py-1 rounded-lg text-xs bg-white/20 backdrop-blur-sm border border-white/30">
                              {summary.active} Active
                            </span>
                          )}
                          {summary.completed > 0 && (
                            <span className="px-3 py-1 rounded-lg text-xs bg-white/20 backdrop-blur-sm border border-white/30">
                              {summary.completed} Done
                            </span>
                          )}
                        </div>
                      )}

                      {/* Bottom Button */}
                      <button
                        onClick={() => handleReferClick(service)}
                        className="
            w-full
            py-4
            rounded-2xl
            bg-white/30
            backdrop-blur-md
            border border-white/40
            font-semibold
            text-white
            transition-all duration-300
            hover:bg-white
            hover:text-gray-900
          "
                      >
                        Refer Student
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ================================================================ */}
          {/* MY SERVICE REQUESTS TAB                                          */}
          {/* ================================================================ */}
          <TabsContent value="requests" className="mt-6">
            {/* Filters */}
            <Card className="p-4 bg-white border-0 shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by student or service..."
                    value={requestSearch}
                    onChange={e => setRequestSearch(e.target.value)}
                    className="pl-10 border-gray-200"
                  />
                </div>
                <Select
                  value={requestStatusFilter}
                  onValueChange={setRequestStatusFilter}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="PENDING_ADMIN_ASSIGNMENT">
                      Pending
                    </SelectItem>
                    <SelectItem value="ASSIGNED">Assigned</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="WAITING_STUDENT">
                      Awaiting Student
                    </SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="ON_HOLD">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {myRequestsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <Card className="p-12 text-center border-0 shadow-sm">
                <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No service requests found
                </h3>
                <p className="text-gray-500 mb-4">
                  {requestSearch || requestStatusFilter !== 'all'
                    ? 'Try adjusting your search or filter'
                    : 'Start referring students from the Services tab'}
                </p>
                {!requestSearch && requestStatusFilter === 'all' && (
                  <Button
                    className="bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => setActiveTab('marketplace')}
                  >
                    Browse Services
                  </Button>
                )}
              </Card>
            ) : (
              <Card className="bg-white border-0 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Student</TableHead>
                        <TableHead className="font-semibold">Service</TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold">
                          Progress
                        </TableHead>
                        <TableHead className="font-semibold">
                          Deadline
                        </TableHead>
                        <TableHead className="font-semibold">
                          Submitted
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRequests.map(req => {
                        const tsc = TABLE_STATUS_CONFIG[req.status];
                        const approvalBadge =
                          req.isAgentInitiated && req.agentApprovalStatus
                            ? APPROVAL_BADGE[req.agentApprovalStatus]
                            : null;
                        const TscIcon = tsc?.icon || AlertCircle;
                        const overdue =
                          req.deadline &&
                          new Date(req.deadline) < new Date() &&
                          req.status !== 'COMPLETED';

                        return (
                          <TableRow
                            key={req.serviceRequestId}
                            className="hover:bg-gray-50"
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-sm font-medium shrink-0">
                                  {req.student?.firstName?.[0] || '?'}
                                  {req.student?.lastName?.[0] || ''}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">
                                    {req.student?.firstName || 'Unknown'}{' '}
                                    {req.student?.lastName || ''}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {req.student?.email || ''}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">
                                {SERVICE_NAME_MAP[req.serviceType] ||
                                  req.serviceType}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {tsc && (
                                  <Badge
                                    className={`${tsc.color} border text-xs w-fit`}
                                  >
                                    <TscIcon className="w-3 h-3 mr-1" />
                                    {tsc.label}
                                  </Badge>
                                )}
                                {approvalBadge && (
                                  <Badge
                                    className={`${approvalBadge.color} border text-xs w-fit`}
                                  >
                                    {approvalBadge.label}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-emerald-500 h-2 rounded-full transition-all"
                                    style={{ width: `${req.progress || 0}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">
                                  {req.progress || 0}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`text-sm ${overdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}
                              >
                                {formatDate(req.deadline)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-500">
                                {formatDate(req.appliedAt || req.createdAt)}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="p-4 border-t bg-gray-50">
                  <p className="text-sm text-gray-500">
                    Showing {filteredRequests.length} of {myRequests.length}{' '}
                    requests
                  </p>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ================================================================== */}
      {/* REFER STUDENT MODAL                                                */}
      {/* ================================================================== */}
      <Dialog open={referModalOpen} onOpenChange={setReferModalOpen}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-600" />
              Refer Student for Service
            </DialogTitle>
            <DialogDescription>
              {selectedService
                ? `Submit a ${selectedService.name} request for one of your students`
                : 'Select a student to refer'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Service header */}
            {selectedService && (
              <div
                className={`rounded-lg p-3 bg-gradient-to-r ${selectedService.gradient} text-white`}
              >
                <div className="flex items-center gap-3">
                  {(() => {
                    const I = selectedService.icon;
                    return <I className="w-6 h-6" />;
                  })()}
                  <div>
                    <p className="font-semibold">{selectedService.name}</p>
                    <p className="text-sm text-white/80">
                      {selectedService.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ---- Student selection ---- */}
            {!showCreateStudent ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Select Student *
                </label>
                <Select
                  value={referForm.studentId}
                  onValueChange={val =>
                    setReferForm(prev => ({ ...prev, studentId: val }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a student..." />
                  </SelectTrigger>
                  <SelectContent>
                    {studentsLoading ? (
                      <div className="p-3 text-center text-sm text-gray-400">
                        Loading students...
                      </div>
                    ) : students.length === 0 ? (
                      <div className="p-3 text-center text-sm text-gray-500">
                        No students assigned to you yet
                      </div>
                    ) : (
                      students.map(student => {
                        const existing = selectedService
                          ? getStudentServiceStatus(
                              student.studentId,
                              selectedService.serviceType,
                            )
                          : null;
                        const state = getButtonState(existing);
                        const isBlocked =
                          existing &&
                          existing.status !== 'CANCELLED' &&
                          state !== 'REJECTED';
                        const display = BUTTON_DISPLAY[state];

                        return (
                          <SelectItem
                            key={student.studentId}
                            value={student.studentId}
                            disabled={isBlocked}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <span className="truncate">
                                {student.firstName} {student.lastName}
                              </span>
                              {isBlocked && display && (
                                <Badge
                                  className={`text-[10px] px-1.5 py-0 ml-auto shrink-0 ${VARIANT_CLASSES[display.variant]}`}
                                >
                                  {display.label}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>

                {/* Create new student toggle */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 mt-1"
                  onClick={() => setShowCreateStudent(true)}
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Create New Student
                </Button>
              </div>
            ) : (
              /* ---- Inline create student form ---- */
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-700">
                    New Student
                  </p>
                  <button
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => setShowCreateStudent(false)}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">
                      First Name *
                    </label>
                    <Input
                      value={newStudentForm.firstName}
                      onChange={e =>
                        setNewStudentForm(p => ({
                          ...p,
                          firstName: e.target.value,
                        }))
                      }
                      placeholder="First name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Last Name *</label>
                    <Input
                      value={newStudentForm.lastName}
                      onChange={e =>
                        setNewStudentForm(p => ({
                          ...p,
                          lastName: e.target.value,
                        }))
                      }
                      placeholder="Last name"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">Email *</label>
                  <Input
                    type="email"
                    value={newStudentForm.email}
                    onChange={e =>
                      setNewStudentForm(p => ({ ...p, email: e.target.value }))
                    }
                    placeholder="student@email.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500">
                    Phone (optional)
                  </label>
                  <Input
                    value={newStudentForm.phone}
                    onChange={e =>
                      setNewStudentForm(p => ({ ...p, phone: e.target.value }))
                    }
                    placeholder="+1 234 567 890"
                    className="mt-1"
                  />
                </div>

                <Button
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                  size="sm"
                  onClick={handleCreateStudent}
                  disabled={creatingStudent}
                >
                  {creatingStudent ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />{' '}
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" /> Create & Select
                      Student
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* ---- Notes ---- */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Notes (optional)
              </label>
              <Textarea
                placeholder="Add any notes or special instructions for this request..."
                value={referForm.notes}
                onChange={e =>
                  setReferForm(prev => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
                className="resize-none"
              />
            </div>

            {/* ---- Document upload ---- */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Documents (optional)
              </label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-emerald-300 transition-colors">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="hidden"
                />
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-1">
                  Upload supporting documents
                </p>
                <p className="text-xs text-gray-400">
                  PDF, JPG, PNG, DOC up to 10 MB each
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose Files
                </Button>
              </div>

              {referForm.documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {referForm.documents.map((file, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-gray-50 rounded-lg p-2.5"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-700 truncate">
                          {file.name}
                        </span>
                        <span className="text-xs text-gray-400 shrink-0">
                          {(file.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <button
                        onClick={() => removeDocument(i)}
                        className="text-gray-400 hover:text-red-500 shrink-0 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReferModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600"
              onClick={handleSubmitReferral}
              disabled={submitting || !referForm.studentId}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{' '}
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
